import { useState, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import {
  Flame,
  Plus,
  Minus,
  Trash2,
  ChefHat,
  Check,
  MoveVertical,
  Clock,
} from 'lucide-react';
import clsx from 'clsx';

interface Modifier {
  id: string;
  name: string;
  priceCents: number;
}

interface OrderItem {
  id: string;
  menuItemId: string;
  name: string;
  shortName?: string;
  priceCents: number;
  quantity: number;
  modifiers: Modifier[];
  specialInstructions?: string;
  seatNumber?: number;
  courseNumber?: number;
  // Server status (only present when loaded from existing check)
  holdUntilFired?: boolean;
  status?: string;
  kitchenStatus?: string;
}

interface CourseDragEditorProps {
  items: OrderItem[];
  onUpdateItemCourse: (itemId: string, courseNumber: number) => void;
  onUpdateItemQuantity: (itemId: string, quantity: number) => void;
  onFireCourse: (courseNumber: number) => void;
  isFirePending?: boolean;
  checkId: string | null;
  formatCurrency: (cents: number) => string;
}

// Draggable item component
function DraggableItem({
  item,
  onUpdateQuantity,
  formatCurrency,
}: {
  item: OrderItem;
  onUpdateQuantity: (quantity: number) => void;
  formatCurrency: (cents: number) => string;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    data: { item },
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;

  // Check item status - fired items are in the kitchen
  const isFired = item.holdUntilFired === false;
  const isUnsaved = item.holdUntilFired === undefined;
  const isWaitingToFire = item.holdUntilFired === true;
  const isInKitchen = isFired || item.kitchenStatus === 'queued' || item.kitchenStatus === 'cooking';
  const isReady = item.kitchenStatus === 'ready';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={clsx(
        'rounded-lg p-3 shadow-sm border mb-2',
        isDragging && 'opacity-30 shadow-lg z-50',
        isReady ? 'bg-emerald-50 border-emerald-300' :
        isInKitchen ? 'bg-slate-100 border-slate-300' :
        isUnsaved ? 'bg-indigo-50 border-indigo-300 border-dashed' :
        isWaitingToFire ? 'bg-amber-50 border-amber-200' :
        'bg-white border-slate-200'
      )}
    >
      <div className="flex items-center gap-2">
        {/* Drag handle - only this triggers drag, disabled for fired items */}
        <button
          {...attributes}
          {...listeners}
          className={clsx(
            'touch-none p-2 -m-1 rounded',
            isFired
              ? 'text-slate-300 cursor-not-allowed'
              : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 active:bg-slate-200'
          )}
          style={{ touchAction: 'none' }}
          disabled={isFired}
        >
          <MoveVertical className="w-5 h-5" />
        </button>

        {/* Status badge */}
        {isUnsaved && (
          <span className="px-2 py-0.5 bg-indigo-500 text-white text-[10px] font-bold rounded">
            NEW
          </span>
        )}
        {isWaitingToFire && !isUnsaved && (
          <span className="px-2 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded">
            WAITING
          </span>
        )}
        {isReady && (
          <span className="px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded">
            READY
          </span>
        )}
        {isInKitchen && !isReady && (
          <span className="px-2 py-0.5 bg-slate-500 text-white text-[10px] font-bold rounded">
            KITCHEN
          </span>
        )}

        {/* Item info */}
        <div className="flex-1 min-w-0">
          <p className={clsx(
            'font-medium text-sm truncate',
            isInKitchen ? 'text-slate-600' : 'text-slate-900'
          )}>
            {item.shortName || item.name}
          </p>
          {item.modifiers.length > 0 && (
            <p className="text-xs text-slate-500 truncate">
              {item.modifiers.map((m) => m.name).join(', ')}
            </p>
          )}
        </div>

        {/* Quantity controls - disabled for fired items */}
        {!isFired ? (
          <div className="flex items-center gap-1">
            <button
              onClick={() => onUpdateQuantity(item.quantity - 1)}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:bg-slate-200"
            >
              {item.quantity === 1 ? (
                <Trash2 className="w-4 h-4 text-red-500" />
              ) : (
                <Minus className="w-4 h-4" />
              )}
            </button>
            <span className="w-6 text-center text-sm font-bold">
              {item.quantity}
            </span>
            <button
              onClick={() => onUpdateQuantity(item.quantity + 1)}
              className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center active:bg-slate-200"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <span className="w-6 text-center text-sm font-bold text-slate-500">
            ×{item.quantity}
          </span>
        )}

        {/* Price */}
        <span className={clsx(
          'text-sm font-semibold ml-2',
          isInKitchen ? 'text-slate-500' : 'text-slate-700'
        )}>
          {formatCurrency(item.priceCents * item.quantity)}
        </span>
      </div>
    </div>
  );
}

// Item preview during drag
function DragPreview({
  item,
  formatCurrency,
}: {
  item: OrderItem;
  formatCurrency: (cents: number) => string;
}) {
  return (
    <div className="bg-white rounded-lg p-3 shadow-2xl border-2 border-primary-500 w-72 rotate-2">
      <div className="flex items-center gap-2">
        <MoveVertical className="w-5 h-5 text-primary-500" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-slate-900 truncate">
            {item.shortName || item.name}
          </p>
          {item.modifiers.length > 0 && (
            <p className="text-xs text-slate-500 truncate">
              {item.modifiers.map((m) => m.name).join(', ')}
            </p>
          )}
        </div>
        <span className="text-sm font-bold">
          {formatCurrency(item.priceCents * item.quantity)}
        </span>
      </div>
    </div>
  );
}

// Droppable course lane
function CourseDropZone({
  courseNumber,
  items,
  onUpdateItemQuantity,
  onFireCourse,
  isFirePending,
  canFire,
  firedCount = 0,
  unsavedCount = 0,
  formatCurrency,
  isOver,
  isDragging,
}: {
  courseNumber: number;
  items: OrderItem[];
  onUpdateItemQuantity: (itemId: string, quantity: number) => void;
  onFireCourse: () => void;
  isFirePending: boolean;
  canFire: boolean;
  firedCount?: number;
  unsavedCount?: number;
  formatCurrency: (cents: number) => string;
  isOver: boolean;
  isDragging: boolean;
}) {
  const { setNodeRef } = useDroppable({
    id: `course-${courseNumber}`,
    data: { courseNumber },
  });

  const [showFired, setShowFired] = useState(false);

  const handleFire = () => {
    onFireCourse();
    setShowFired(true);
    setTimeout(() => setShowFired(false), 2000);
  };

  return (
    <div
      ref={setNodeRef}
      className={clsx(
        'rounded-xl overflow-hidden transition-all duration-200',
        isOver && 'ring-2 ring-primary-500 ring-offset-2 scale-[1.02]',
        isDragging && !isOver && 'opacity-80'
      )}
    >
      {/* Course header */}
      <div
        className={clsx(
          'px-4 py-3 flex items-center justify-between transition-colors',
          showFired ? 'bg-emerald-600' : 'bg-gradient-to-r from-slate-700 to-slate-600'
        )}
      >
        <div className="flex items-center gap-3">
          <span className="bg-white/20 text-white font-bold text-sm px-3 py-1 rounded-full">
            {courseNumber}
          </span>
          <span className="text-white font-medium">
            Course {courseNumber}
          </span>
          <span className="text-white/70 text-sm">
            {items.length} item{items.length !== 1 ? 's' : ''}
          </span>
        </div>

        {showFired ? (
          <div className="flex items-center gap-2 text-white">
            <Check className="w-5 h-5" />
            <span className="font-medium">Sent to Kitchen!</span>
          </div>
        ) : firedCount > 0 && !canFire ? (
          // All items in this course are already fired
          <div className="flex items-center gap-2 text-emerald-300">
            <ChefHat className="w-4 h-4" />
            <span className="text-sm font-medium">In Kitchen</span>
          </div>
        ) : unsavedCount > 0 && !canFire ? (
          // Items need to be saved first
          <div className="flex items-center gap-2 text-amber-300">
            <span className="text-xs font-medium">Save order first</span>
          </div>
        ) : canFire ? (
          <button
            onClick={handleFire}
            disabled={isFirePending}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-full font-medium text-sm transition-all',
              'bg-white text-orange-600 hover:bg-orange-50 active:scale-95',
              isFirePending && 'opacity-50'
            )}
          >
            <Flame className="w-4 h-4" />
            Fire Course
          </button>
        ) : null}
      </div>

      {/* Items area */}
      <div
        className={clsx(
          'p-3 bg-slate-50 min-h-[80px] transition-colors',
          isOver && 'bg-primary-50'
        )}
      >
        {items.length === 0 ? (
          <div
            className={clsx(
              'border-2 border-dashed rounded-lg py-6 text-center transition-colors',
              isOver
                ? 'border-primary-400 bg-primary-100 text-primary-600'
                : 'border-slate-300 text-slate-400'
            )}
          >
            <MoveVertical className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">
              {isOver ? 'Drop here!' : 'Drag items here'}
            </p>
          </div>
        ) : (
          items.map((item) => (
            <DraggableItem
              key={item.id}
              item={item}
              onUpdateQuantity={(qty) => onUpdateItemQuantity(item.id, qty)}
              formatCurrency={formatCurrency}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default function CourseDragEditor({
  items,
  onUpdateItemCourse,
  onUpdateItemQuantity,
  onFireCourse,
  isFirePending = false,
  checkId,
  formatCurrency,
}: CourseDragEditorProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overCourseId, setOverCourseId] = useState<string | null>(null);

  // Group items by course
  const itemsByCourse = new Map<number, OrderItem[]>();
  for (const item of items) {
    const course = item.courseNumber || 1;
    if (!itemsByCourse.has(course)) {
      itemsByCourse.set(course, []);
    }
    itemsByCourse.get(course)!.push(item);
  }

  // Get max course - always show at least 1, plus one empty for new items
  const maxCourse = items.length > 0
    ? Math.max(...items.map((i) => i.courseNumber || 1))
    : 1;

  // Always show existing courses + 1 empty one when dragging
  const coursesToShow = Array.from({ length: maxCourse + (activeId ? 1 : 0) }, (_, i) => i + 1);

  // Sensors configured for touch devices
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    })
  );

  const activeItem = activeId ? items.find((i) => i.id === activeId) : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    if (over) {
      setOverCourseId(String(over.id));
    } else {
      setOverCourseId(null);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverCourseId(null);

    if (!over) return;

    const draggedItem = items.find((i) => i.id === active.id);
    if (!draggedItem) return;

    // Get target course from droppable id
    const overId = String(over.id);
    if (overId.startsWith('course-')) {
      const newCourse = parseInt(overId.replace('course-', ''));
      if (draggedItem.courseNumber !== newCourse) {
        onUpdateItemCourse(draggedItem.id, newCourse);
      }
    }
  };

  // Calculate status counts for summary
  const unsavedItems = items.filter(i => i.holdUntilFired === undefined);
  const unfiredItems = items.filter(i => i.holdUntilFired === true);
  const firedItems = items.filter(i => i.holdUntilFired === false);

  if (items.length === 0) {
    return (
      <div className="text-center text-slate-500 py-12">
        <ChefHat className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p className="font-medium">No items yet</p>
        <p className="text-sm">Tap menu items to add</p>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="space-y-4 pb-4">
        {/* Status Summary */}
        <div className="bg-white rounded-xl p-3 shadow-sm">
          <div className="flex items-center justify-between gap-2 text-xs">
            {firedItems.length > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full">
                <ChefHat className="w-3.5 h-3.5" />
                <span className="font-medium">{firedItems.length} in kitchen</span>
              </div>
            )}
            {unfiredItems.length > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-amber-100 text-amber-700 rounded-full">
                <Flame className="w-3.5 h-3.5" />
                <span className="font-medium">{unfiredItems.length} ready to fire</span>
              </div>
            )}
            {unsavedItems.length > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                <Plus className="w-3.5 h-3.5" />
                <span className="font-medium">{unsavedItems.length} new</span>
              </div>
            )}
          </div>
          {unsavedItems.length > 0 && (
            <p className="text-xs text-slate-500 mt-2 text-center">
              Tap "Add Items" below to save new items, then fire courses
            </p>
          )}
        </div>

        {/* Instructions */}
        <div className="flex items-center justify-center gap-2 text-xs text-slate-500 bg-white rounded-lg py-2 px-3">
          <MoveVertical className="w-4 h-4" />
          <span>Drag items between courses</span>
          <span className="text-slate-300">|</span>
          <Flame className="w-4 h-4 text-orange-500" />
          <span>Tap Fire to send to kitchen</span>
        </div>

        {/* Course lanes */}
        {coursesToShow.map((courseNum) => {
          const courseItems = itemsByCourse.get(courseNum) || [];
          // Can only fire if there are items with holdUntilFired: true (unfired items)
          // Items with holdUntilFired undefined are new local items that need to be saved first
          const unfiredItems = courseItems.filter(i => i.holdUntilFired === true);
          const firedItems = courseItems.filter(i => i.holdUntilFired === false);
          const unsavedItems = courseItems.filter(i => i.holdUntilFired === undefined);

          return (
            <CourseDropZone
              key={courseNum}
              courseNumber={courseNum}
              items={courseItems}
              onUpdateItemQuantity={onUpdateItemQuantity}
              onFireCourse={() => onFireCourse(courseNum)}
              isFirePending={isFirePending}
              canFire={!!checkId && unfiredItems.length > 0}
              firedCount={firedItems.length}
              unsavedCount={unsavedItems.length}
              formatCurrency={formatCurrency}
              isOver={overCourseId === `course-${courseNum}`}
              isDragging={!!activeId}
            />
          );
        })}

        {/* Add new course button when dragging */}
        {activeId && (
          <div className="border-2 border-dashed border-primary-300 rounded-xl p-6 text-center bg-primary-50">
            <Plus className="w-6 h-6 mx-auto mb-2 text-primary-500" />
            <p className="text-sm font-medium text-primary-600">
              Drop here for Course {maxCourse + 1}
            </p>
          </div>
        )}
      </div>

      {/* Drag overlay - shows item being dragged */}
      <DragOverlay dropAnimation={null}>
        {activeItem ? (
          <DragPreview item={activeItem} formatCurrency={formatCurrency} />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}
